import axios from 'axios';
import { storeAction } from "../storeActions.constant";
import { storeGetters } from "../storeGetters.constant";
import { storeMutation } from "../storeMutations.constant";
import { getBasicHeaders } from "../../common/utils/utils";
import { wrapAction } from "../../common/utils/actionLifecycle";
import {
    contentDirectoryPageConstant,
    createContentState,
    defaultContentSort,
} from "../../components/ContentDirectoryPage/contentDirectoryPage.constant";
import { defaultDirectorySearchObject } from "../store.constant";
import { getAxiosCancelTokenSource } from "../../app.utils";
import uniqBy from 'lodash/uniqBy';

const contentDirectoryState = () => {
    return {
        baseURL: process.env.VUE_APP_BASE_URL,
        contentDirectorySearchResult: null,
        contentState: null,
        contentSelectedFilters: createContentState(),
        contentDirectorySort: null,
        contentSearchText: null,
        searchResultOffset: 0,
        disableContentDirectoryInfiniteScroll: false,
        selectedLetterFilter: null,
    };
};
const contentDirectoryPageMutations = {
    [storeMutation.setContentDirectorySearchText]: (state, data) => {
        state.contentSearchText = data;
    },
    [storeMutation.setContentDirectorySearchResults]: (state, data) => {
        state.contentDirectorySearchResult = {
            ...data,
            searchResults: data.searchResults || [],
            searchRibbonGroup: data.searchRibbonGroup || [],
        };
        state.searchResultOffset = contentDirectoryPageConstant.constant.searchResultOffsetStep;
    },
    [storeMutation.appendToContentDirectorySearchResults]: (state, data) => {
        if (data && data.searchResults && data.searchResults.length) {
            state.contentDirectorySearchResult = {
                ...data,
                searchResults: [...(state.contentDirectorySearchResult.searchResults || []), ...data.searchResults],
            };

            state.contentDirectorySearchResult.searchResults = uniqBy(state.contentDirectorySearchResult.searchResults, 'id');
            state.searchResultOffset += contentDirectoryPageConstant.constant.searchResultOffsetStep;
        }
    },
    [storeMutation.setContentDirectorySort]: (state, value) => {
        state.contentDirectorySort = value;
        if (window.sessionStorage) {
            sessionStorage.contentDirectorySort = JSON.stringify(value);
        }
    },
    [storeMutation.setContentFiltersInState]: (state, filterData) => {
        const { key, filters } = filterData;
        if (state.contentSelectedFilters && key && filters) {
            state.contentSelectedFilters[key] = [...filters];
        }
    },
    [storeMutation.setContentStateFromRouter]: (state, dataFromRoute) => {
        const { contentSearchText, filters } = dataFromRoute;
        state.contentSelectedFilters = createContentState();
        if (filters) {
            state.contentSelectedFilters = { ...state.contentSelectedFilters, ...filters };
        }
        state.contentSearchText = contentSearchText || '';
    },
    [storeMutation.removeContentPillFromState]: (state, pill) => {
        if (state.contentSelectedFilters) {
            Object.keys(state.contentSelectedFilters).forEach((ribbonItem) => {
                if (state.contentSelectedFilters.hasOwnProperty(ribbonItem)) {
                    const itemFound = state.contentSelectedFilters[ribbonItem].find(item => item.filterKey === pill.filterKey && item.filterValue === pill.filterValue);
                    if (itemFound) {
                        state.contentSelectedFilters[ribbonItem].splice(state.contentSelectedFilters[ribbonItem].indexOf(itemFound), 1);
                    }
                }
            });
            state.contentState = { ...state.contentSelectedFilters };
        }
    },
    [storeMutation.setDisableContentDirectoryInfiniteScroll]: (state, val) => {
        state.disableContentDirectoryInfiniteScroll = val;
    },
    [storeMutation.setContentSelectedLetterFilter]: (state, filter) => {
        state.selectedLetterFilter = filter || null;
    },
};

const contentDirectoryPageActions = {
    [storeAction.getContentAutoCompleteSearchResults]: wrapAction(
        storeAction.getContentAutoCompleteSearchResults,
        ({ state, commit }) => {
            //handling race condition
            const requestSource = getAxiosCancelTokenSource();
            const requestObject = {
                actionName: storeAction.getContentAutoCompleteSearchResults,
                requestSource,
            };
            commit(storeMutation.handleCancelableRequest, requestObject, { root: true });

            return axios({
                url: `${state.baseURL}content/api/contentsearchautocomplete`,
                headers: getBasicHeaders(),
                cancelToken: requestSource.token,
                data: { search: state.contentSearchText },
                method: 'POST',
            }).then((response) => {
                commit(storeMutation.removeCancelableRequest, storeAction.getContentAutoCompleteSearchResults, { root: true });
                return response;
            }).catch((err) => {
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        },
    ),

    [storeAction.getInitialContentDirectorySearchResults]: wrapAction(
        storeAction.getInitialContentDirectorySearchResults,
        ({ state, commit, dispatch }) => {
            commit(storeMutation.setDisableContentDirectoryInfiniteScroll, true);

            return dispatch(storeAction.getContentDirectorySearchResults, 0)
                .then((response) => {
                    commit(storeMutation.setContentDirectorySearchResults, response.data);

                    //handling infinite scroll
                    if (response && response.data && response.data.searchResults
                        && response.data.searchResults.length
                        && response.data.totalResultCount
                        && state.contentDirectorySearchResult.searchResults
                        && response.data.totalResultCount > state.contentDirectorySearchResult.searchResults.length) {
                        commit(storeMutation.setDisableContentDirectoryInfiniteScroll, false);
                    }

                    return response;
                })
                .catch((err) => {
                    commit(storeMutation.setDisableContentDirectoryInfiniteScroll, false);
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        },
    ),
    [storeAction.getNextContentDirectorySearchResults]: wrapAction(
        storeAction.getNextContentDirectorySearchResults,
        ({ state, commit, dispatch }) => {
            commit(storeMutation.setDisableContentDirectoryInfiniteScroll, true);

            return dispatch(storeAction.getContentDirectorySearchResults, state.searchResultOffset)
                .then((response) => {
                    commit(storeMutation.appendToContentDirectorySearchResults, response.data);

                    //handling infinite scroll
                    if (response && response.data && response.data.searchResults
                        && response.data.searchResults.length
                        && response.data.totalResultCount
                        && state.contentDirectorySearchResult.searchResults
                        && response.data.totalResultCount > state.contentDirectorySearchResult.searchResults.length) {
                        commit(storeMutation.setDisableContentDirectoryInfiniteScroll, false);
                    }

                    return response;
                })
                .catch((err) => {
                    commit(storeMutation.setDisableContentDirectoryInfiniteScroll, false);
                    //TODO: handle error
                    console.log(err);
                    throw err;
                });
        },
    ),

    [storeAction.getContentDirectorySearchResults]: wrapAction(
        storeAction.getContentDirectorySearchResults,
        ({ state, getters }, skip) => {
            const sortObject = getters[storeGetters.contentDirectorySort];
            const sortString = `${sortObject.sortField} ${sortObject.direction}`;
            const requestBody = defaultDirectorySearchObject(
                state.contentSearchText,
                sortString,
                state.contentSelectedFilters,
                skip,
                contentDirectoryPageConstant.constant.searchResultMaxCount,
            );
            return axios({
                //url: '/mocks/contentDirectory.json',
                url: `${state.baseURL}content/api/contentSearch`,
                headers: getBasicHeaders(),
                data: requestBody,
                //method: 'GET',
                method: 'POST',
            });
        },
    ),

    [storeAction.clearAllContentFilters]: ({ state }) => {
        state.contentSelectedFilters = createContentState();
        state.contentState = { ...state.contentSelectedFilters };
    },
};

const contentDirectoryPageGetters = {
    [storeGetters.contentDirectorySearchText]: state => state.contentSearchText,
    [storeGetters.contentDirectorySearchResults]: state => state.contentDirectorySearchResult,
    [storeGetters.contentState]: state => state.contentState,
    [storeGetters.contentSelectedFilters]: state => state.contentSelectedFilters,
    [storeGetters.contentDirectorySort]: (state) => {
        if (state.contentDirectorySort) return state.contentDirectorySort;
        if (window.sessionStorage && sessionStorage.contentDirectorySort) {
            try {
                const sort = JSON.parse(sessionStorage.contentDirectorySort);
                if (sort) return sort;
            } catch (e) { /* pass */ }
        }

        return defaultContentSort;
    },
    [storeGetters.disableContentDirectoryInfiniteScroll]: state => state.disableContentDirectoryInfiniteScroll,
    [storeGetters.nextContentDirectorySearchOffset]: state => state.searchResultOffset,
    [storeGetters.contentDirectorySelectedLetterFilter]: state => state.selectedLetterFilter,
};

export default {
    namespaced: true,
    state: {
        initialState: contentDirectoryState(),
        ...contentDirectoryState(),
    },
     mutations: { ...contentDirectoryPageMutations },
     actions: { ...contentDirectoryPageActions },
     getters: { ...contentDirectoryPageGetters },
};
